import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import WhatsAppFloat from './components/WhatsAppFloat';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Collaborations from './pages/Collaborations';
import Contact from './pages/Contact';
import Career from './pages/Career';
import Projects from './pages/Projects';
import ProgramProjects from './pages/ProgramProjects';
import MbaProjects from './pages/MbaProjects';
import UniversityDetail from './pages/UniversityDetail';
import MbaGuidelines from './pages/MbaGuidelines';
import PremiumPreviewPage from './pages/PremiumPreviewPage';
import Team from './pages/Team';
import AboutStory from './pages/AboutStory';
import AboutVision from './pages/AboutVision';
import AdminLogin from './pages/AdminLogin';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminApplications from './pages/admin/AdminApplications';
import AdminContacts from './pages/admin/AdminContacts';
import AdminJobs from './pages/admin/AdminJobs';
import AdminNewsletter from './pages/admin/AdminNewsletter';
import AdminProjects from './pages/admin/AdminProjects';
import AdminMbaProjects from './pages/admin/AdminMbaProjects';
import AdminAddProject from './pages/admin/AdminAddProject';
import AdminServices from './pages/admin/AdminServices';
import AdminTeam from './pages/admin/AdminTeam';
import AdminHome from './pages/admin/AdminHome';
import AdminContactInfo from './pages/admin/AdminContactInfo';
import AdminGallery from './pages/admin/AdminGallery';
import AdminSocial from './pages/admin/AdminSocial';
import AdminTestimonials from './pages/admin/AdminTestimonials';
import AdminCollaborations from './pages/admin/AdminCollaborations';
import AdminEnquiries from './pages/admin/AdminEnquiries';
import AdminEnquiryDetail from './pages/admin/AdminEnquiryDetail';
import AdminCompletedEnquiries from './pages/admin/AdminCompletedEnquiries';
import AdminPaymentEnquiries from './pages/admin/AdminPaymentEnquiries';
import AdminUniversities from './pages/admin/AdminUniversities';
import AdminInternships from './pages/admin/AdminInternships';
import AdminInternshipDetail from './pages/admin/AdminInternshipDetail';
import AdminCustomSoftwareEnquiries from './pages/admin/AdminCustomSoftwareEnquiries';
import AdminCustomSoftwareEnquiryDetail from './pages/admin/AdminCustomSoftwareEnquiryDetail';
import AdminCustomMbaEnquiries from './pages/admin/AdminCustomMbaEnquiries';
import AdminCustomMbaEnquiryDetail from './pages/admin/AdminCustomMbaEnquiryDetail';
import './styles/globals.css';
import './styles/App.scss';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';

function App() {
  return (
    <HelmetProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ScrollToTop />
        <ErrorBoundary>
          <Routes>
            {/* Admin Login - No navbar/footer */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/login" element={<Navigate to="/" replace />} />

            {/* Admin Routes with Layout */}
            <Route path="/admin/dashboard" element={<ProtectedRoute><AdminLayout><AdminDashboard /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/applications" element={<ProtectedRoute><AdminLayout><AdminApplications /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/contacts" element={<ProtectedRoute><AdminLayout><AdminContacts /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/jobs" element={<ProtectedRoute><AdminLayout><AdminJobs /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/newsletter" element={<ProtectedRoute><AdminLayout><AdminNewsletter /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/projects/add" element={<ProtectedRoute><AdminLayout><AdminAddProject /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/projects" element={<ProtectedRoute><AdminLayout><AdminProjects /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/mba-projects" element={<ProtectedRoute><AdminLayout><AdminMbaProjects /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/mba-universities" element={<ProtectedRoute><AdminLayout><AdminUniversities /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/services" element={<ProtectedRoute><AdminLayout><AdminServices /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/team" element={<ProtectedRoute><AdminLayout><AdminTeam /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/home" element={<ProtectedRoute><AdminLayout><AdminHome /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/contact-info" element={<ProtectedRoute><AdminLayout><AdminContactInfo /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/gallery" element={<ProtectedRoute><AdminLayout><AdminGallery /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/social" element={<ProtectedRoute><AdminLayout><AdminSocial /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/testimonials" element={<ProtectedRoute><AdminLayout><AdminTestimonials /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/collaborations" element={<ProtectedRoute><AdminLayout><AdminCollaborations /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/mba-enquiries" element={<ProtectedRoute><AdminLayout><AdminEnquiries type="mba" /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/mba-enquiries/:id" element={<ProtectedRoute><AdminLayout><AdminEnquiryDetail type="mba" /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/software-enquiries" element={<ProtectedRoute><AdminLayout><AdminEnquiries type="software" /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/software-enquiries/:id" element={<ProtectedRoute><AdminLayout><AdminEnquiryDetail type="software" /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/completed-enquiries" element={<ProtectedRoute><AdminLayout><AdminCompletedEnquiries /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/payment-enquiries" element={<ProtectedRoute><AdminLayout><AdminPaymentEnquiries /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/internship-applications" element={<ProtectedRoute><AdminLayout><AdminInternships /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/internship-applications/:id" element={<ProtectedRoute><AdminLayout><AdminInternshipDetail /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/custom-software-enquiries" element={<ProtectedRoute><AdminLayout><AdminCustomSoftwareEnquiries /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/custom-software-enquiries/:id" element={<ProtectedRoute><AdminLayout><AdminCustomSoftwareEnquiryDetail /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/custom-mba-enquiries" element={<ProtectedRoute><AdminLayout><AdminCustomMbaEnquiries /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/custom-mba-enquiries/:id" element={<ProtectedRoute><AdminLayout><AdminCustomMbaEnquiryDetail /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/enquiries" element={<Navigate to="/admin/mba-enquiries" replace />} />
            <Route path="/admin" element={<Navigate to="/admin/login" replace />} />

            {/* Public Routes with navbar/footer */}
            <Route path="/premium-preview/:id" element={<PremiumPreviewPage />} />
            <Route path="/*" element={<MainRoutes />} />
          </Routes>
        </ErrorBoundary>
      </Router>
    </HelmetProvider>
  );
}

function MainRoutes() {
  return (
    <>
      <Navbar />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/about/story" element={<AboutStory />} />
          <Route path="/about/vision-mission" element={<AboutVision />} />
          <Route path="/services" element={<Services />} />
          <Route path="/collaborations" element={<Collaborations />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/career" element={<Career />} />
          <Route path="/projects" element={<Projects />} />
          {/* detailed project page removed; use quick-view modal from lists instead */}
          <Route path="/program-projects" element={<ProgramProjects />} />
          <Route path="/mba-projects" element={<MbaProjects />} />
          <Route path="/mba-guidelines" element={<MbaGuidelines />} />
          <Route path="/mba-projects/:slug" element={<UniversityDetail />} />
          <Route path="/team" element={<Team />} />
          <Route path="/about/our-team" element={<Team />} />

        </Routes>
      </main>
      <Footer />
      <WhatsAppFloat />
    </>
  );
}

export default App;
